"use strict";
/**
 * Which deployment a process is, on Northflank or locally. Every app reads its
 * hosting identity through these functions rather than from `process.env`, so
 * the variable names live in one place.
 *
 * `NEWFORM_ENVIRONMENT` is the one human-set value: Northflank injects no
 * environment name, so each environment's restricted secret group sets it
 * (`production`, `staging`, `Staging-Jeff`, …). It must never sit in a shared
 * secret group. A copied production value is what once let a staging slot run
 * production's cron catalog against copied client data (2026-08-17). Values
 * keep the old platform names because database rows store them (launch runtime
 * ownership, the production calendar sync's `enabledOnEnvironment`).
 *
 * The `NF_*` values are injected by Northflank into every running container
 * (https://northflank.com/docs/v1/application/secure/inject-secrets), so their
 * presence is what marks a process as hosted. Local development has none of
 * them and reads as non-production.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.deploymentEnvironmentName = deploymentEnvironmentName;
exports.deploymentIdentity = deploymentIdentity;
exports.isHostedRuntime = isHostedRuntime;
exports.hostedServiceName = hostedServiceName;
exports.hostedProjectId = hostedProjectId;
exports.deploymentCommitSha = deploymentCommitSha;
exports.hostedPublicHostnames = hostedPublicHostnames;
exports.shutdownGraceSecondsRaw = shutdownGraceSecondsRaw;
exports.isStagingJeffEnvironment = isStagingJeffEnvironment;
function present(value) {
    const trimmed = value === null || value === void 0 ? void 0 : value.trim();
    return trimmed ? trimmed : null;
}
function deploymentEnvironmentName(env = process.env) {
    return present(env.NEWFORM_ENVIRONMENT);
}
function deploymentIdentity(env = process.env) {
    var _a, _b;
    return {
        environmentName: (_a = deploymentEnvironmentName(env)) !== null && _a !== void 0 ? _a : undefined,
        gitBranch: (_b = present(env.NF_DEPLOYMENT_BRANCH)) !== null && _b !== void 0 ? _b : undefined,
    };
}
/** True inside a Northflank container, whatever the environment. */
function isHostedRuntime(env = process.env) {
    return present(env.NF_OBJECT_ID) !== null;
}
/** The Northflank service (or job) name, e.g. `web-server`. */
function hostedServiceName(env = process.env) {
    return present(env.NF_OBJECT_ID);
}
function hostedProjectId(env = process.env) {
    return present(env.NF_PROJECT_ID);
}
function deploymentCommitSha(env = process.env) {
    return present(env.NF_DEPLOYMENT_SHA);
}
/**
 * Public hostnames Northflank routes to this service: its generated
 * `*.code.run` names plus any custom domains.
 */
function hostedPublicHostnames(env = process.env) {
    var _a;
    return ((_a = env.NF_HOSTS) !== null && _a !== void 0 ? _a : "")
        .split(",")
        .map((host) => host.trim())
        .filter(Boolean);
}
/**
 * Seconds between SIGTERM and SIGKILL. Northflank does not expose its grace
 * period to the container, so `SHUTDOWN_GRACE_SECONDS` is set beside the
 * service's termination grace setting and must match it. Raw, because callers
 * apply their own fallback and record what the environment said.
 */
function shutdownGraceSecondsRaw(env = process.env) {
    return env.SHUTDOWN_GRACE_SECONDS;
}
/** Exact isolated preview environment; a named environment always wins. */
function isStagingJeffEnvironment(environment) {
    var _a, _b;
    const environmentName = (_a = environment.environmentName) === null || _a === void 0 ? void 0 : _a.trim().toLowerCase();
    const branch = (_b = environment.gitBranch) === null || _b === void 0 ? void 0 : _b.trim().toLowerCase();
    return (environmentName === "staging-jeff" ||
        (!environmentName && branch === "staging-jeff"));
}
